import { useNavigate } from 'react-router';
import { TeamShirt, Typography } from '@dreamteam-monorepo/ui';

import { useTeamCreateTournamentCalendarId } from '@/hooks/useTeamCreateTournamentCalendarId';
import { useCurrentMatchdayWindow } from '@/hooks/useCurrentMatchday';
import { useFetchPlayer } from '@/hooks/useFetchPlayer';

import { BEPlayerStatsItem } from '@/lib/network';
import { periodAxisFromFormat } from '@/lib/periodLabels';

import { LockedPlayerInfoModal } from '@/pages/Teams/components/LockedPlayerInfoModal';
import { toUiPlayerFromStatsItem } from '@/lib/players/toUiPlayer';

import { useModalsStore } from '@/stores/ModalsStore';
import { usePastPeriodStore } from '@/stores/PastPeriodStore';

import { CATEGORY_COLUMNS, PPM_COLUMNS, STATS_COLUMNS } from './constants';
import { normalizePosition } from './helpers';

function getPlayerStatValue(player: BEPlayerStatsItem, field: keyof BEPlayerStatsItem): number {
  const value =
    field === 'gameweekPoints'
      ? (player.gameweekPoints ?? player.matchdayPoints ?? 0)
      : player[field];

  return typeof value === 'number' && Number.isFinite(value) ? value : 0;
}

function formatPlayerStatCell(player: BEPlayerStatsItem, field: keyof BEPlayerStatsItem): string {
  // Only show a ‘Clean Sheet’ if the player is a DEF or GK
  if (field === 'cleanSheet' && player.position && !['DEF', 'GK'].includes(player.position))
    return '';

  const value = getPlayerStatValue(player, field);
  if (field === 'price') return `£${value.toFixed(1)}m`;
  // Pass completion is a rate: a 0 means "no meaningful sample" (didn't play, or below
  // the minimum pass attempts to qualify), not a genuine 0% — show a dash, not "0".
  if (field === 'passCompletionRate' && value <= 0) return '—';
  return String(Number(value.toFixed(1)));
}

export function PlayerRow({
  player,
  activeCategoryKeys,
  activePpmKeys,
}: {
  player: BEPlayerStatsItem;
  activeCategoryKeys: string[];
  activePpmKeys: string[];
}) {
  const navigate = useNavigate();
  const { playerModalId, isPlayerModalOpen, setIsPlayerModalOpen } = useModalsStore();
  const { selectedPastPeriodId } = usePastPeriodStore();

  const {
    id: TEAM_CREATE_TOURNAMENT_CALENDAR_ID,
    format: tournamentFormat,
    hasResolved: hasResolvedTournamentCalendar,
  } = useTeamCreateTournamentCalendarId();

  const { data: currentPeriodDataData } = useCurrentMatchdayWindow(
    TEAM_CREATE_TOURNAMENT_CALENDAR_ID as string,
    {
      enabled: hasResolvedTournamentCalendar && !!TEAM_CREATE_TOURNAMENT_CALENDAR_ID,
      source: 'team',
      format:
        tournamentFormat === 'classic_season_long' || tournamentFormat === 'knockout'
          ? tournamentFormat
          : undefined,
    }
  );

  const isGameweekFormat = periodAxisFromFormat(tournamentFormat) === 'gameweek';

  const currentPlayerType = isGameweekFormat ? 'gameweek' : 'matchday';

  const currentPeriodId = isGameweekFormat
    ? currentPeriodDataData?.currentGameweek?.id || currentPeriodDataData?.nextGameweek?.id
    : currentPeriodDataData?.currentMatchday?.id || currentPeriodDataData?.nextMatchday?.id;

  const lockedPlayerPeriodId = selectedPastPeriodId || currentPeriodId;

  const { data: lockedPlayerDetails } = useFetchPlayer({
    playerId: player.playerId ? String(player.playerId) : undefined,
    periodId: lockedPlayerPeriodId,
    periodType: currentPlayerType,
    options: { enabled: !!player.playerId && !!lockedPlayerPeriodId },
  });

  const pos = normalizePosition(player.position);
  const activeCategoryColumns = CATEGORY_COLUMNS.filter(col =>
    activeCategoryKeys.includes(col.key)
  );
  const activePpmColumns = PPM_COLUMNS.filter(col => activePpmKeys.includes(col.key));

  return (
    <>
      <button
        onClick={() => setIsPlayerModalOpen(player.playerId)}
        className="flex items-center border-b border-[#eae9fd] px-4 py-3 hover:bg-[#f5f5ff] transition-colors w-full"
      >
        <div className="flex-shrink-0 w-[240px] flex items-center gap-3">
          <TeamShirt
            team={player.shirtKey ?? undefined}
            position={pos}
            assetScale="1x"
            className="h-8 w-8 shrink-0"
          />
          <div className="flex flex-col min-w-0">
            <span className="text-sm font-bold text-[#030033] leading-tight truncate">
              {player.displayName}
            </span>
            <span className="text-xs text-[#888]">
              {player.contestantShortName ?? player.contestantName ?? '—'} · {pos}
            </span>
          </div>
        </div>
        {[...STATS_COLUMNS, ...activeCategoryColumns, ...activePpmColumns].map(col => (
          <div key={col.key} className="flex-1 flex items-center justify-center">
            <Typography variant="paragraph020" className="text-[#030033]">
              {formatPlayerStatCell(player, col.field)}
            </Typography>
          </div>
        ))}
      </button>

      <LockedPlayerInfoModal
        open={isPlayerModalOpen && playerModalId === player.playerId}
        onOpenChange={() => setIsPlayerModalOpen(null)}
        player={toUiPlayerFromStatsItem(player)}
        lockedPlayerDetails={lockedPlayerDetails}
        onFullProfile={() => {
          setIsPlayerModalOpen(null);
          navigate(`/player/${player.playerId}`);
        }}
      />
    </>
  );
}
